// JOGI LAP (Impresszum · Adatkezelési tájékoztató) — próza-elrendezés, i18n-ből.
//
// MIÉRT EGY KOMPONENS KÉT LAPRA: a két dokumentum SZERKEZETE azonos (címzett szakaszok, bekezdés vagy
// felsorolás), csak a tartalom más. A tartalom i18n-ből jön (nincs beégetett szöveg), és TÖMBKÉNT: egy jogi
// szöveg bekezdés-listája nem UI-felirat, amit kulcsonként érdemes szétvágni — a szakasz a fordítás egysége.
//
// A LAP-CÍMET a TOPBAR viszi (cím + „Frissítve: …" alcím), ezért a lap-testben NINCS megismételve — a terv
// (`design/partner/screens/Profile.jsx` `LegalPage`) is így oldja meg. Kétszer kiírva a cím fölösleges zaj
// volt (mérve az élő lapon).
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '@tanstack/react-router';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

/**
 * Egy szakasz: cím + bekezdés(ek). Egynél több bekezdés → felsorolás (a terv `prose` szabálya).
 *
 * Az `ordered` és a `links` a LEGACY CRM jogi lapjai miatt kell: az adatkezelési tájékoztató SZÁMOZOTT
 * pontokból áll (a pontokra a szöveg hivatkozik, tehát a sorszám tartalom, nem stílus), és két jogszabály-
 * hivatkozást tartalmaz, amelyek URL-je NEM szerepel a szövegben — azokat külön kell kiírni.
 */
export interface LegalBlock {
  h: string;
  p: string[];
  /** `true` → számozott lista (`<ol>`) a felsorolás helyett. */
  ordered?: boolean;
  /** A szakasz alatti, kattintható hivatkozások (külső jogszabály-link). */
  links?: { label: string; url: string }[];
}

/**
 * E-MAIL és TELEFONSZÁM a próza-szövegben — kattinthatóan (a legacy lapon is linkek voltak).
 *
 * MIÉRT ITT, ÉS MIÉRT NEM A SZÖVEGBEN: a jogi szöveg a legacy rendszerből jön, mondat-szinten; az
 * elérhetőség a mondat RÉSZE („E-mail cím: sales@morgens.hu"), nem külön adat. Ha külön link-listát
 * tennénk alá, ugyanaz a cím kétszer állna a lapon. A minta szűk és determinisztikus (e-mail + `+36…`
 * telefonszám), és NEM tud HTML-t beszúrni: a szöveget React-elemekre vágjuk, nem `dangerouslySetInnerHTML`-lel.
 */
const CONTACT_PATTERN = /([\w.+-]+@[\w-]+\.[\w.]+)|(\+36[\d ]{7,})/g;

function withContactLinks(text: string): ReactNode {
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(CONTACT_PATTERN)) {
    const at = m.index;
    if (at > last) out.push(text.slice(last, at));
    const raw = m[0];
    const isEmail = m[1] !== undefined;
    const href = isEmail ? `mailto:${raw.trim()}` : `tel:${raw.replace(/\s/g, '')}`;
    out.push(
      <a
        key={`${href}-${String(at)}`}
        href={href}
        className="text-brand hover:text-brand-hover underline underline-offset-2 transition-colors"
      >
        {raw.trim()}
      </a>,
    );
    // A telefon-minta a záró szóközt/írásjelet is elnyelheti — a nyers hosszal léptetünk, hogy ne veszítsünk.
    last = at + raw.length;
    if (raw !== raw.trim()) out.push(raw.slice(raw.trimEnd().length));
  }
  if (last < text.length) out.push(text.slice(last));
  return out.map((n, i) => <Fragment key={i}>{n}</Fragment>);
}

export function PortalLegalPage({
  blocksKey,
}: {
  /** A szakaszok tömbjének i18n-kulcsa (a lap címét és a frissítés-dátumot a topbar viszi). */
  blocksKey: string;
}): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // A szakasz-lista TÖMB az i18n-ből (`returnObjects`). A cast dokumentált: a típusos `t` a lapos
  // string-kulcsokra van hangolva, a jogi szöveg viszont szerkezetet hordoz.
  const blocks = t(blocksKey, { returnObjects: true }) as unknown as LegalBlock[];

  return (
    <div className="w-full max-w-3xl">
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 mb-4"
        onClick={() => void navigate({ to: '/portal/profile' })}
      >
        <ArrowLeftIcon />
        {t('portal.nav.profile')}
      </Button>

      <div className="max-w-[65ch] text-sm leading-[1.75]">
        {(Array.isArray(blocks) ? blocks : []).map((b) => (
          <section key={b.h} className="mt-7 first:mt-0">
            <h2 className="mb-2 text-[17px] font-semibold tracking-[-0.01em]">{b.h}</h2>
            {b.p.length > 1 ? (
              b.ordered === true ? (
                <ol className="text-muted-foreground list-decimal space-y-2 pl-5">
                  {b.p.map((p) => (
                    <li key={p}>{withContactLinks(p)}</li>
                  ))}
                </ol>
              ) : (
                <ul className="text-muted-foreground list-disc space-y-1.5 pl-5">
                  {b.p.map((p) => (
                    <li key={p}>{withContactLinks(p)}</li>
                  ))}
                </ul>
              )
            ) : (
              <p className="text-muted-foreground">{withContactLinks(b.p[0] ?? '')}</p>
            )}
            {b.links !== undefined && b.links.length > 0 && (
              <ul className="mt-2.5 space-y-1 pl-0">
                {b.links.map((l) => (
                  <li key={l.url}>
                    <a
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-brand hover:text-brand-hover underline underline-offset-2 transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
